/** Sidebar navigation contract — policy data for shell nav. */

import { HUB_PERMISSION_KEYS, type HubPermission } from "@/lib/auth/permissions";
import { ROUTES, workTasksMyListPath } from "./route-paths";

export type NavSectionId =
  | "platform"
  | "intake"
  | "customer"
  | "conversations"
  | "party-master"
  | "supplier-ops"
  | "partner-ops"
  | "work"
  | "strategy"
  | "contracts"
  | "finance"
  | "hrm"
  | "content"
  | "policy-rules"
  | "ai-safety"
  | "template-governance"
  | "platform-administration"
  | "knowledge"
  | "knowledge-data-governance"
  | "observability"
  | "context-governance"
  | "user-workspace"
  | "templates"
  | "rules"
  | "workspace"
  | "administration"
  | "ai-platform"
  | "iam-access";

export type PlatformModule =
  | "Platform Shell"
  | "Intake Core"
  | "Customer Ops"
  | "Customer Intelligence"
  | "Commercial Parties"
  | "Work Orchestration"
  | "Executive Strategy"
  | "Contracts"
  | "Finance"
  | "Tenant Business — Finance"
  | "Human Resources"
  | "Content Plane"
  | "Platform Governance"
  | "Platform Policy & Admin"
  | "Policy & Admin"
  | "IAM Authority"
  | "User Workspace"
  | "Knowledge"
  | "Knowledge Platform"
  | "Knowledge Data Governance"
  | "Data Governance"
  | "Platform Observability"
  | "AI Platform";

export type SidebarNavIconId =
  | "dashboard"
  | "inbox"
  | "view"
  | "clock"
  | "recordsExplorer"
  | "operationsDigest"
  | "consultation"
  | "workAnalytics"
  | "projects"
  | "hubData"
  | "customers"
  | "tasks"
  | "accountSettings"
  | "mySettings"
  | "draftTemplates"
  | "triageTemplates"
  | "threadDigestTemplates"
  | "requestInfoTemplates"
  | "aiAudit"
  | "aiPrivacyRules"
  | "llmPrivacy"
  | "platformAdministration"
  | "adminOperational"
  | "adminIntegrations"
  | "adminSyncDiagnostics"
  | "contextGovernance"
  | "knowledge"
  | "knowledgePlatform"
  | "knowledgeCorpus"
  | "knowledgePreRagReview"
  | "knowledgeIngest"
  | "knowledgeContent"
  | "knowledgeIndexOps"
  | "contextCatalog"
  | "tenantEnablement"
  | "catalogDrift"
  | "projectionOps"
  | "iamAuthz"
  | "iamAuthzRoles"
  | "iamAuthzUsers"
  | "systemHealth"
  | "workflowAiPolicy"
  | "policyRules"
  | "governanceRules"
  | "strategy"
  | "contracts"
  | "ledger"
  | "ranking"
  | "platformAnalytics"
  | "monitoring"
  | "userGuide";

export interface SidebarNavItem {
  section: NavSectionId;
  platformModule: PlatformModule;
  labelKey: string;
  href: string;
  iconId: SidebarNavIconId;
  parentHref?: string;
  requiredPermissions?: readonly HubPermission[];
  paletteHintKey?: string;
}

export const PERM_TASKS_EXECUTE = [HUB_PERMISSION_KEYS.TASKS_EXECUTE] as const;
export const PERM_TASKS_MANAGE = [HUB_PERMISSION_KEYS.TASKS_MANAGE] as const;
export const PERM_DRAFT_READ = [HUB_PERMISSION_KEYS.DRAFT_READ] as const;
export const PERM_INTAKE_ITEMS_READ = [HUB_PERMISSION_KEYS.INTAKE_ITEMS_READ] as const;
/** Platform Search — knowledge / records / content read (any-of). */
export const PERM_PLATFORM_SEARCH = ["rag.documents.read", "content.items.read"] as const;
/** Observability L1 + analytics/monitoring leaves (org.ai.operate fe_routes). */
export const PERM_OBSERVABILITY_READ = ["ai.analytics.read"] as const;
export const PERM_INTAKE_LEDGER_READ = [HUB_PERMISSION_KEYS.INTAKE_LEDGER_READ] as const;
export const PERM_CONVERSATION_OPS_READ = [HUB_PERMISSION_KEYS.CONVERSATION_OPS_READ] as const;
export const PERM_COMMERCIAL_PARTY_READ = [HUB_PERMISSION_KEYS.COMMERCIAL_PARTY_READ] as const;
export const PERM_ADMIN_ACCESS = [HUB_PERMISSION_KEYS.ADMIN_ACCESS] as const;
export const PERM_SETTINGS_INTEGRATIONS_READ = ["settings.integrations.read"] as const;
export const PERM_PROMPT_TEMPLATES_READ = ["governance.prompt_templates.read"] as const;
export const PERM_STRATEGY_DASHBOARD_READ = ["strategy.dashboard.read"] as const;
export const PERM_STRATEGY_GOALS_READ = ["strategy.goals.read"] as const;
export const RANKING_REQUIRED_PERMISSIONS = ["rag.documents.read", "ranking.vector.embed", "ranking.rank.execute"] as const;
export const ANALYSIS_REQUIRED_PERMISSIONS = ["ai.system.write"] as const;

/** Single source for sidebar and command palette; order follows the platform navigation contract. */
export const SIDEBAR_NAV: readonly SidebarNavItem[] = [
  {
    section: "intake",
    platformModule: "Intake Core",
    labelKey: "nav.intakeDashboard",
    href: ROUTES.INTAKE_DASHBOARD,
    iconId: "dashboard",
    requiredPermissions: PERM_INTAKE_ITEMS_READ,
  },
  {
    section: "platform",
    platformModule: "Platform Shell",
    labelKey: "nav.search",
    href: ROUTES.SEARCH,
    iconId: "recordsExplorer",
    paletteHintKey: "palette.hint.platformSearch",
    requiredPermissions: PERM_PLATFORM_SEARCH,
  },
  {
    section: "intake",
    platformModule: "Intake Core",
    labelKey: "nav.recordsExplorer",
    href: ROUTES.INTAKE_RECORDS,
    iconId: "recordsExplorer",
    requiredPermissions: PERM_INTAKE_ITEMS_READ,
    paletteHintKey: "palette.hint.recordsExplorer",
  },
  { section: "intake", platformModule: "Intake Core", labelKey: "nav.inbox", href: ROUTES.INTAKE_DRAFTS, iconId: "inbox", requiredPermissions: PERM_DRAFT_READ, paletteHintKey: "palette.hint.inbox" },
  {
    section: "intake",
    platformModule: "Intake Core",
    labelKey: "nav.operationsDigest",
    href: ROUTES.INTAKE_DIGEST,
    iconId: "operationsDigest",
    requiredPermissions: PERM_DRAFT_READ,
    paletteHintKey: "palette.hint.operationsDigest",
  },
  {
    section: "work",
    platformModule: "Work Orchestration",
    labelKey: "nav.projects",
    href: ROUTES.WORK_PROJECTS,
    iconId: "projects",
    requiredPermissions: [HUB_PERMISSION_KEYS.PROJECTS_READ],
  },
  {
    section: "work",
    platformModule: "Work Orchestration",
    labelKey: "nav.tasks",
    href: ROUTES.WORK_TASKS,
    iconId: "tasks",
    requiredPermissions: PERM_TASKS_EXECUTE,
  },
  {
    section: "work",
    platformModule: "Work Orchestration",
    labelKey: "nav.customView",
    href: ROUTES.WORK_CUSTOM_VIEW,
    iconId: "view",
    requiredPermissions: PERM_TASKS_MANAGE,
  },
  {
    section: "work",
    platformModule: "Work Orchestration",
    labelKey: "nav.workAnalytics",
    href: ROUTES.WORK_ANALYTICS,
    iconId: "workAnalytics",
    requiredPermissions: PERM_TASKS_EXECUTE,
    paletteHintKey: "palette.hint.workAnalytics",
  },
  {
    section: "conversations",
    platformModule: "Intake Core",
    labelKey: "nav.conversations",
    href: ROUTES.CONVERSATIONS,
    iconId: "consultation",
    requiredPermissions: PERM_CONVERSATION_OPS_READ,
    paletteHintKey: "palette.hint.consultation",
  },
  {
    section: "conversations",
    platformModule: "Intake Core",
    labelKey: "nav.channelScope",
    href: ROUTES.CONVERSATIONS_CHANNEL_SCOPE,
    iconId: "consultation",
    requiredPermissions: PERM_CONVERSATION_OPS_READ,
    paletteHintKey: "palette.hint.workScope",
  },
  {
    section: "party-master",
    platformModule: "Commercial Parties",
    labelKey: "nav.customerDirectory",
    href: ROUTES.PARTIES,
    iconId: "customers",
    requiredPermissions: PERM_COMMERCIAL_PARTY_READ,
    paletteHintKey: "palette.hint.hubData",
  },
  {
    section: "party-master",
    platformModule: "Commercial Parties",
    labelKey: "nav.identityReview",
    href: ROUTES.PARTY_IDENTITY_REVIEW,
    iconId: "customers",
    requiredPermissions: PERM_COMMERCIAL_PARTY_READ,
    paletteHintKey: "palette.hint.identityReview",
  },
  {
    section: "supplier-ops",
    platformModule: "Commercial Parties",
    labelKey: "nav.supplierDirectory",
    href: ROUTES.SUPPLIER_DIRECTORY,
    iconId: "customers",
    requiredPermissions: PERM_COMMERCIAL_PARTY_READ,
    paletteHintKey: "palette.hint.hubData",
  },
  {
    section: "partner-ops",
    platformModule: "Commercial Parties",
    labelKey: "nav.partnerDirectory",
    href: ROUTES.PARTNER_DIRECTORY,
    iconId: "customers",
    requiredPermissions: PERM_COMMERCIAL_PARTY_READ,
    paletteHintKey: "palette.hint.hubData",
  },
  {
    section: "strategy",
    platformModule: "Executive Strategy",
    labelKey: "nav.overview",
    href: ROUTES.STRATEGY,
    iconId: "strategy",
    requiredPermissions: PERM_STRATEGY_DASHBOARD_READ,
    paletteHintKey: "palette.hint.strategyOverview",
  },
  {
    section: "strategy",
    platformModule: "Executive Strategy",
    labelKey: "nav.goals",
    href: ROUTES.STRATEGY_GOALS,
    iconId: "strategy",
    requiredPermissions: PERM_STRATEGY_GOALS_READ,
    paletteHintKey: "palette.hint.strategyGoals",
  },
  {
    section: "strategy",
    platformModule: "Executive Strategy",
    labelKey: "nav.alignment",
    href: ROUTES.STRATEGY_ALIGNMENT,
    iconId: "strategy",
    requiredPermissions: PERM_STRATEGY_GOALS_READ,
    paletteHintKey: "palette.hint.strategyAlignment",
  },
  {
    section: "strategy",
    platformModule: "Executive Strategy",
    labelKey: "nav.reviews",
    href: ROUTES.STRATEGY_REVIEWS,
    iconId: "strategy",
    requiredPermissions: PERM_STRATEGY_GOALS_READ,
    paletteHintKey: "palette.hint.strategyReviews",
  },
  { section: "user-workspace", platformModule: "User Workspace", labelKey: "nav.accountSettings", href: ROUTES.USER_ACCOUNT_SETTINGS, iconId: "accountSettings" },
  { section: "user-workspace", platformModule: "User Workspace", labelKey: "nav.mailSettings", href: ROUTES.USER_ACCOUNT_SETTINGS_MAIL, iconId: "accountSettings" },
  { section: "user-workspace", platformModule: "User Workspace", labelKey: "nav.passwordSettings", href: ROUTES.USER_ACCOUNT_SETTINGS_PASSWORD, iconId: "accountSettings" },
  { section: "user-workspace", platformModule: "User Workspace", labelKey: "nav.zaloConnection", href: ROUTES.USER_ACCOUNT_SETTINGS_ZALO, iconId: "accountSettings" },
  {
    section: "contracts",
    platformModule: "Contracts",
    labelKey: "nav.contracts",
    href: ROUTES.CONTRACTS_AGREEMENTS,
    iconId: "contracts",
    requiredPermissions: ["contracts.agreements.read"],
    paletteHintKey: "palette.hint.contracts",
  },
  {
    section: "contracts",
    platformModule: "Contracts",
    labelKey: "nav.contractTemplates",
    href: ROUTES.CONTRACTS_TEMPLATES,
    iconId: "contracts",
    requiredPermissions: ["contracts.templates.read"],
    paletteHintKey: "palette.hint.contracts",
  },
  {
    section: "finance",
    platformModule: "Tenant Business — Finance",
    labelKey: "nav.financeReceivables",
    href: ROUTES.FINANCE_INVOICES,
    iconId: "ledger",
    requiredPermissions: ["finance.invoices.read"],
    paletteHintKey: "palette.hint.finance",
  },
  {
    section: "finance",
    platformModule: "Tenant Business — Finance",
    labelKey: "nav.financePayables",
    href: ROUTES.FINANCE_PAYABLES,
    iconId: "ledger",
    requiredPermissions: ["finance.payables.read"],
    paletteHintKey: "palette.hint.finance",
  },
  {
    section: "finance",
    platformModule: "Tenant Business — Finance",
    labelKey: "nav.financePaymentDossiers",
    href: ROUTES.FINANCE_BILLING,
    iconId: "ledger",
    requiredPermissions: ["finance.payment_dossiers.read"],
    paletteHintKey: "palette.hint.finance",
  },
  {
    section: "finance",
    platformModule: "Tenant Business — Finance",
    labelKey: "nav.financeBankAccounts",
    href: ROUTES.FINANCE_BANK,
    iconId: "ledger",
    requiredPermissions: ["finance.bank_accounts.read", "finance.bank_transactions.read"],
    paletteHintKey: "palette.hint.finance",
  },
  {
    section: "finance",
    platformModule: "Tenant Business — Finance",
    labelKey: "nav.financeBankTransactions",
    href: ROUTES.FINANCE_BANK_TRANSACTIONS,
    iconId: "ledger",
    requiredPermissions: ["finance.bank_accounts.read", "finance.bank_transactions.read"],
    paletteHintKey: "palette.hint.finance",
  },
  {
    section: "finance",
    platformModule: "Tenant Business — Finance",
    labelKey: "nav.financeReconciliation",
    href: ROUTES.FINANCE_RECONCILIATION,
    iconId: "ledger",
    requiredPermissions: ["finance.reconciliation.read"],
    paletteHintKey: "palette.hint.finance",
  },
  {
    section: "finance",
    platformModule: "Tenant Business — Finance",
    labelKey: "nav.financeAccountingReports",
    href: ROUTES.FINANCE_REPORTING,
    iconId: "ledger",
    requiredPermissions: [
      "finance.ar_aging.read",
      "finance.journal_entries.read",
      "finance.events.read",
      "finance.invoices.read",
    ],
    paletteHintKey: "palette.hint.finance",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLegalEntities",
    href: ROUTES.HRM_LEGAL_ENTITIES,
    iconId: "customers",
    requiredPermissions: ["hrm.org.legal_entity.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmCompanyLevels",
    href: ROUTES.HRM_COMPANY_LEVELS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.company_level.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmBusinessLines",
    href: ROUTES.HRM_BUSINESS_LINES,
    iconId: "customers",
    requiredPermissions: ["hrm.org.business_line.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEmployeeGroups",
    href: ROUTES.HRM_EMPLOYEE_GROUPS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.employee_group.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSpecialties",
    href: ROUTES.HRM_SPECIALTIES,
    iconId: "customers",
    requiredPermissions: ["hrm.org.specialty.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTitleGroups",
    href: ROUTES.HRM_TITLE_GROUPS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.title_group.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmJobFamilies",
    href: ROUTES.HRM_JOB_FAMILIES,
    iconId: "customers",
    requiredPermissions: ["hrm.org.job_family.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmJobGroups",
    href: ROUTES.HRM_JOB_GROUPS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.job_group.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOrgLevels",
    href: ROUTES.HRM_ORG_LEVELS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.org_level.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTitles",
    href: ROUTES.HRM_TITLES,
    iconId: "customers",
    requiredPermissions: ["hrm.org.title.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmJobs",
    href: ROUTES.HRM_JOBS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.job.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOrgUnits",
    href: ROUTES.HRM_ORG_UNITS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.org_unit.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPositions",
    href: ROUTES.HRM_POSITIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.position.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHeadcounts",
    href: ROUTES.HRM_HEADCOUNTS,
    iconId: "customers",
    requiredPermissions: ["hrm.org.headcount.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmNationalities",
    href: ROUTES.HRM_NATIONALITIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.nationality.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEthnicities",
    href: ROUTES.HRM_ETHNICITIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.ethnicity.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmBanks",
    href: ROUTES.HRM_BANKS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.bank.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEducationLevels",
    href: ROUTES.HRM_EDUCATION_LEVELS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.education_level.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmProfessionalTitles",
    href: ROUTES.HRM_PROFESSIONAL_TITLES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.professional_title.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEmployeeTypes",
    href: ROUTES.HRM_EMPLOYEE_TYPES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.employee_type.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmStandardWorkdays",
    href: ROUTES.HRM_STANDARD_WORKDAYS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.standard_workday.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAttendanceObjects",
    href: ROUTES.HRM_ATTENDANCE_OBJECTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.attendance_object.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAttendanceWorkTypes",
    href: ROUTES.HRM_ATTENDANCE_WORK_TYPES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.attendance_work_type.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAttendanceSymbols",
    href: ROUTES.HRM_ATTENDANCE_SYMBOLS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.attendance_symbol.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAttendanceHolidays",
    href: ROUTES.HRM_ATTENDANCE_HOLIDAYS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.attendance_holiday.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAttendanceShifts",
    href: ROUTES.HRM_ATTENDANCE_SHIFTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.attendance_shift.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAttendanceShiftSchedules",
    href: ROUTES.HRM_ATTENDANCE_SHIFT_SCHEDULES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.attendance_shift_schedule.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOtTimeWindows",
    href: ROUTES.HRM_OT_TIME_WINDOWS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.ot_time_window.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOtReasons",
    href: ROUTES.HRM_OT_REASONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.ot_reason.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOtPlans",
    href: ROUTES.HRM_OT_PLANS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.ot_plan.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOtPlanLines",
    href: ROUTES.HRM_OT_PLAN_LINES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.ot_plan_line.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDutyConversionEntries",
    href: ROUTES.HRM_DUTY_CONVERSION_ENTRIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.duty_conversion_entry.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTimesheetDailyEntries",
    href: ROUTES.HRM_TIMESHEET_DAILY_ENTRIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.timesheet_daily_entry.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLateEarlyViolations",
    href: ROUTES.HRM_LATE_EARLY_VIOLATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.late_early_violation.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDutyFundLedgers",
    href: ROUTES.HRM_DUTY_FUND_LEDGERS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.duty_fund_ledger.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmCompLeaveFundLedgers",
    href: ROUTES.HRM_COMP_LEAVE_FUND_LEDGERS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.comp_leave_fund_ledger.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmFundOpeningBalances",
    href: ROUTES.HRM_FUND_OPENING_BALANCES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.fund_opening_balance.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmCollectiveBusinessTrips",
    href: ROUTES.HRM_COLLECTIVE_BUSINESS_TRIPS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.collective_business_trip.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmCollectiveBusinessTripLines",
    href: ROUTES.HRM_COLLECTIVE_BUSINESS_TRIP_LINES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.collective_business_trip_line.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTitlePolicies",
    href: ROUTES.HRM_TITLE_POLICIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.title_policy.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmBirthdayPlans",
    href: ROUTES.HRM_BIRTHDAY_PLANS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.birthday_plan.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmKeyBirthdayPlans",
    href: ROUTES.HRM_KEY_BIRTHDAY_PLANS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.key_birthday_plan.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmReligions",
    href: ROUTES.HRM_RELIGIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.religion.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmMaritalStatuses",
    href: ROUTES.HRM_MARITAL_STATUSES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.marital_status.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHealthcarePlaces",
    href: ROUTES.HRM_HEALTHCARE_PLACES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.healthcare_place.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTrainingSchools",
    href: ROUTES.HRM_TRAINING_SCHOOLS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.training_school.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmStudyMajors",
    href: ROUTES.HRM_STUDY_MAJORS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.study_major.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPracticeForms",
    href: ROUTES.HRM_PRACTICE_FORMS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.practice_form.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPracticeDurations",
    href: ROUTES.HRM_PRACTICE_DURATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.practice_duration.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDoctorCompetencies",
    href: ROUTES.HRM_DOCTOR_COMPETENCIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.doctor_competency.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOccupations",
    href: ROUTES.HRM_OCCUPATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.occupation.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHospitals",
    href: ROUTES.HRM_HOSPITALS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.hospital.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHealthStatuses",
    href: ROUTES.HRM_HEALTH_STATUSES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.health_status.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHealthExamClassifications",
    href: ROUTES.HRM_HEALTH_EXAM_CLASSIFICATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.health_exam_classification.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOccupationalDiseases",
    href: ROUTES.HRM_OCCUPATIONAL_DISEASES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.occupational_disease.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmForeignLanguageLevels",
    href: ROUTES.HRM_FOREIGN_LANGUAGE_LEVELS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.foreign_language_level.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmComputerSkillLevels",
    href: ROUTES.HRM_COMPUTER_SKILL_LEVELS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.computer_skill_level.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmWorkLocations",
    href: ROUTES.HRM_WORK_LOCATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.work_location.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmBusinessTripReasons",
    href: ROUTES.HRM_BUSINESS_TRIP_REASONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.business_trip_reason.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmContractGroups",
    href: ROUTES.HRM_CONTRACT_GROUPS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.contract_group.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmContractTypes",
    href: ROUTES.HRM_CONTRACT_TYPES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.contract_type.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmConstitutionBases",
    href: ROUTES.HRM_CONSTITUTION_BASES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.constitution_base.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDecisionBases",
    href: ROUTES.HRM_DECISION_BASES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.decision_base.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDecisionTypes",
    href: ROUTES.HRM_DECISION_TYPES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.decision_type.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEstablishmentReasons",
    href: ROUTES.HRM_ESTABLISHMENT_REASONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.establishment_reason.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEstablishmentDecisions",
    href: ROUTES.HRM_ESTABLISHMENT_DECISIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.establishment_decision.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDirectiveBases",
    href: ROUTES.HRM_DIRECTIVE_BASES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.directive_base.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDisciplineForms",
    href: ROUTES.HRM_DISCIPLINE_FORMS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.discipline_form.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmResignationReasons",
    href: ROUTES.HRM_RESIGNATION_REASONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.resignation_reason.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmRewardForms",
    href: ROUTES.HRM_REWARD_FORMS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.reward_form.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTrainingClassifications",
    href: ROUTES.HRM_TRAINING_CLASSIFICATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.training_classification.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTrainingSystems",
    href: ROUTES.HRM_TRAINING_SYSTEMS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.training_system.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmClinicalSpecialties",
    href: ROUTES.HRM_CLINICAL_SPECIALTIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.clinical_specialty.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSpecialtyDiseaseClassifications",
    href: ROUTES.HRM_SPECIALTY_DISEASE_CLASSIFICATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.specialty_disease_classification.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSalaryChangeReasons",
    href: ROUTES.HRM_SALARY_CHANGE_REASONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.salary_change_reason.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPositionChangeReasons",
    href: ROUTES.HRM_POSITION_CHANGE_REASONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.position_change_reason.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTechnicalSpecialties",
    href: ROUTES.HRM_TECHNICAL_SPECIALTIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.technical_specialty.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDmktRegistrationCampaigns",
    href: ROUTES.HRM_DMKT_REGISTRATION_CAMPAIGNS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.dmkt_registration_campaign.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmCertificates",
    href: ROUTES.HRM_CERTIFICATES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.certificate.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDiplomas",
    href: ROUTES.HRM_DIPLOMAS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.diploma.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHealthRecords",
    href: ROUTES.HRM_HEALTH_RECORDS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.health_record.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmProcedureDossiers",
    href: ROUTES.HRM_PROCEDURE_DOSSIERS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.procedure_dossier.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEvaluationRecords",
    href: ROUTES.HRM_EVALUATION_RECORDS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.evaluation_record.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOnboardingRecords",
    href: ROUTES.HRM_ONBOARDING_RECORDS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.onboarding_record.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmJobDescriptionRecords",
    href: ROUTES.HRM_JOB_DESCRIPTION_RECORDS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.job_description_record.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTrainingHistories",
    href: ROUTES.HRM_TRAINING_HISTORIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.training_history.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmContracts",
    href: ROUTES.HRM_CONTRACTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.contract.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmWorkHistories",
    href: ROUTES.HRM_WORK_HISTORIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.work_history.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSalaryDecisions",
    href: ROUTES.HRM_SALARY_DECISIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.salary_decision.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmFamilyRelations",
    href: ROUTES.HRM_FAMILY_RELATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.family_relation.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLeaveRecords",
    href: ROUTES.HRM_LEAVE_RECORDS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.leave_record.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDisciplineHistories",
    href: ROUTES.HRM_DISCIPLINE_HISTORIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.discipline_history.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmRewardHistories",
    href: ROUTES.HRM_REWARD_HISTORIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.reward_history.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmBusinessTrips",
    href: ROUTES.HRM_BUSINESS_TRIPS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.business_trip.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmMaternityHistories",
    href: ROUTES.HRM_MATERNITY_HISTORIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.maternity_history.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmConcurrentRoles",
    href: ROUTES.HRM_CONCURRENT_ROLES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.concurrent_role.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSocialInsuranceHistories",
    href: ROUTES.HRM_SOCIAL_INSURANCE_HISTORIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.social_insurance_history.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEndConcurrentRoles",
    href: ROUTES.HRM_END_CONCURRENT_ROLES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.end_concurrent_role.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLateEarlyRegimes",
    href: ROUTES.HRM_LATE_EARLY_REGIMES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.late_early_regime.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSupplementalLeaveGrants",
    href: ROUTES.HRM_SUPPLEMENTAL_LEAVE_GRANTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.supplemental_leave_grant.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLeaveYearBalances",
    href: ROUTES.HRM_LEAVE_YEAR_BALANCES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.leave_year_balance.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmCompensatoryLeaveGrants",
    href: ROUTES.HRM_COMPENSATORY_LEAVE_GRANTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.compensatory_leave_grant.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPracticeCertificates",
    href: ROUTES.HRM_PRACTICE_CERTIFICATES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.practice_certificate.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPeriodicHealthExams",
    href: ROUTES.HRM_PERIODIC_HEALTH_EXAMS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.periodic_health_exam.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHepatitisBVaccinations",
    href: ROUTES.HRM_HEPATITIS_B_VACCINATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.hepatitis_b_vaccination.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmCooperations",
    href: ROUTES.HRM_COOPERATIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.cooperation.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmWorkAccidents",
    href: ROUTES.HRM_WORK_ACCIDENTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.work_accident.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEmployeeFiles",
    href: ROUTES.HRM_EMPLOYEE_FILES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.employee_file.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOccupationalDiseaseCases",
    href: ROUTES.HRM_OCCUPATIONAL_DISEASE_CASES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.occupational_disease_case.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPracticeMentorships",
    href: ROUTES.HRM_PRACTICE_MENTORSHIPS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.practice_mentorship.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPracticeGuidanceActive",
    href: ROUTES.HRM_PRACTICE_GUIDANCE_ACTIVE,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.practice_guidance_active.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPracticeMentorEligibility",
    href: ROUTES.HRM_PRACTICE_MENTOR_ELIGIBILITY,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.practice_mentor_eligibility.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSalaryProposals",
    href: ROUTES.HRM_SALARY_PROPOSALS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.salary_proposal.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmTechnicalCompetencies",
    href: ROUTES.HRM_TECHNICAL_COMPETENCIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.technical_competency.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLeaveCarryoverExtensions",
    href: ROUTES.HRM_LEAVE_CARRYOVER_EXTENSIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.leave_carryover_extension.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmRegionalMinimumWages",
    href: ROUTES.HRM_REGIONAL_MINIMUM_WAGES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.regional_minimum_wage.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSiParticipationRates",
    href: ROUTES.HRM_SI_PARTICIPATION_RATES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.si_participation_rate.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAttendancePeriods",
    href: ROUTES.HRM_ATTENDANCE_PERIODS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.attendance_period.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPayrollPeriods",
    href: ROUTES.HRM_PAYROLL_PERIODS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.payroll_period.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPayrollItemGroups",
    href: ROUTES.HRM_PAYROLL_ITEM_GROUPS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.payroll_item_group.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPayrollItems",
    href: ROUTES.HRM_PAYROLL_ITEMS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.payroll_item.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPayrollItemFormulas",
    href: ROUTES.HRM_PAYROLL_ITEM_FORMULAS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.payroll_item_formula.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSiMonthlyContributions",
    href: ROUTES.HRM_SI_MONTHLY_CONTRIBUTIONS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.si_monthly_contribution.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmExternalIncomeIndividuals",
    href: ROUTES.HRM_EXTERNAL_INCOME_INDIVIDUALS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.external_income_individual.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmExternalIncomeBlocks",
    href: ROUTES.HRM_EXTERNAL_INCOME_BLOCKS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.external_income_block.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmSalaryPlanEntries",
    href: ROUTES.HRM_SALARY_PLAN_ENTRIES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.salary_plan_entry.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmClassifiedRevenues",
    href: ROUTES.HRM_CLASSIFIED_REVENUES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.classified_revenue.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmKy1BenefitRates",
    href: ROUTES.HRM_KY1_BENEFIT_RATES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.ky1_benefit_rate.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHospitalMonthlyRevenues",
    href: ROUTES.HRM_HOSPITAL_MONTHLY_REVENUES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.hospital_monthly_revenue.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPayrollSnapshots",
    href: ROUTES.HRM_PAYROLL_SNAPSHOTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.payroll_snapshot.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLeaveRequests",
    href: ROUTES.HRM_LEAVE_REQUESTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.leave_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLeaveRequestApprovalSteps",
    href: ROUTES.HRM_LEAVE_REQUEST_APPROVAL_STEPS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.leave_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmBusinessTripRequests",
    href: ROUTES.HRM_BUSINESS_TRIP_REQUESTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.business_trip_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOtRequests",
    href: ROUTES.HRM_OT_REQUESTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.ot_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOtRequestApprovalSteps",
    href: ROUTES.HRM_OT_REQUEST_APPROVAL_STEPS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.ot_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmOtRequestEditEvents",
    href: ROUTES.HRM_OT_REQUEST_EDIT_EVENTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.ot_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmRequestAttachments",
    href: ROUTES.HRM_REQUEST_ATTACHMENTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.request_attachment.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmLeaveBalances",
    href: ROUTES.HRM_LEAVE_BALANCES,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.leave_balance.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmPortalTimesheetDailyEntries",
    href: ROUTES.HRM_PORTAL_TIMESHEET_DAILY_ENTRIES,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.timesheet_daily_entry.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAttendanceExplanationRequests",
    href: ROUTES.HRM_ATTENDANCE_EXPLANATION_REQUESTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.attendance_explanation_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDmktRegistrationRequests",
    href: ROUTES.HRM_DMKT_REGISTRATION_REQUESTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.dmkt_registration_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDmktRegistrationLines",
    href: ROUTES.HRM_DMKT_REGISTRATION_LINES,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.dmkt_registration_line.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDmktRegistrationApprovalSteps",
    href: ROUTES.HRM_DMKT_REGISTRATION_APPROVAL_STEPS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.dmkt_registration_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmDmktRegistrationEditEvents",
    href: ROUTES.HRM_DMKT_REGISTRATION_EDIT_EVENTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.dmkt_registration_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmResignationRequests",
    href: ROUTES.HRM_RESIGNATION_REQUESTS,
    iconId: "customers",
    requiredPermissions: ["hrm.portal.resignation_request.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAlerts",
    href: ROUTES.HRM_ALERTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.alert.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmFormLayouts",
    href: ROUTES.HRM_FORM_LAYOUTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.alert.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmReportLayouts",
    href: ROUTES.HRM_REPORT_LAYOUTS,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.alert.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAbrPhase",
    href: ROUTES.HRM_ABR_PHASE,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.abr_phase.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmAdLdap",
    href: ROUTES.HRM_AD_LDAP,
    iconId: "customers",
    requiredPermissions: ["hrm.integration.ad_ldap.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmWebhooks",
    href: ROUTES.HRM_WEBHOOKS,
    iconId: "customers",
    requiredPermissions: ["hrm.integration.webhook.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmHisRevenue",
    href: ROUTES.HRM_HIS_REVENUE,
    iconId: "customers",
    requiredPermissions: ["hrm.integration.his_revenue.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmNfrSecurity",
    href: ROUTES.HRM_NFR_SECURITY,
    iconId: "customers",
    requiredPermissions: ["hrm.integration.nfr_security.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "hrm",
    platformModule: "Human Resources",
    labelKey: "nav.hrmEmployees",
    href: ROUTES.HRM_EMPLOYEES,
    iconId: "customers",
    requiredPermissions: ["hrm.hr.employee.read"],
    paletteHintKey: "palette.hint.hrm",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.contentCapture",
    href: ROUTES.KNOWLEDGE_CONTENT_CAPTURE,
    iconId: "knowledgePlatform",
    requiredPermissions: ["content.items.write"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.knowledgePreRagReview",
    href: ROUTES.KNOWLEDGE_PRE_RAG_REVIEW,
    iconId: "knowledgePlatform",
    requiredPermissions: ["rag.documents.read", "knowledge.ops.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.knowledgeCorpus",
    href: ROUTES.KNOWLEDGE_CORPUS,
    iconId: "knowledgePlatform",
    requiredPermissions: ["rag.documents.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.search",
    href: ROUTES.SEARCH,
    iconId: "knowledgePlatform",
    requiredPermissions: ["rag.documents.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.knowledgeIngest",
    href: ROUTES.KNOWLEDGE_INGEST,
    iconId: "knowledgePlatform",
    parentHref: ROUTES.KNOWLEDGE_PLATFORM,
    requiredPermissions: ["ingest.sources.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.knowledgeContent",
    href: ROUTES.KNOWLEDGE_CONTENT,
    iconId: "knowledgePlatform",
    parentHref: ROUTES.KNOWLEDGE_PLATFORM,
    requiredPermissions: ["content.items.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.contentReview",
    href: ROUTES.KNOWLEDGE_CONTENT_REVIEW,
    iconId: "knowledgePlatform",
    parentHref: ROUTES.KNOWLEDGE_PLATFORM,
    requiredPermissions: ["content.items.read", "content.review.approve"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.contentLibrary",
    href: ROUTES.KNOWLEDGE_CONTENT_LIBRARY,
    iconId: "knowledgePlatform",
    parentHref: ROUTES.KNOWLEDGE_PLATFORM,
    requiredPermissions: ["content.items.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.contentStorage",
    href: ROUTES.KNOWLEDGE_CONTENT_STORAGE,
    iconId: "knowledgePlatform",
    parentHref: ROUTES.KNOWLEDGE_PLATFORM,
    requiredPermissions: ["content.items.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.indexOperations",
    href: ROUTES.KNOWLEDGE_OPS,
    iconId: "knowledgePlatform",
    requiredPermissions: ["rag.statistics.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.knowledgeEmbeddings",
    href: ROUTES.KNOWLEDGE_EMBEDDINGS,
    iconId: "knowledgePlatform",
    parentHref: ROUTES.KNOWLEDGE_PLATFORM,
    requiredPermissions: ["rag.embeddings.write"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.knowledgeIndexDiagnostics",
    href: ROUTES.KNOWLEDGE_SEARCH_LAB,
    iconId: "knowledgePlatform",
    parentHref: ROUTES.KNOWLEDGE_PLATFORM,
    requiredPermissions: ["rag.documents.read"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "knowledge",
    platformModule: "Knowledge",
    labelKey: "nav.knowledgeEvaluation",
    href: ROUTES.KNOWLEDGE_EVALUATION,
    iconId: "knowledgePlatform",
    parentHref: ROUTES.KNOWLEDGE_PLATFORM,
    requiredPermissions: ["rag.evaluation.write"],
    paletteHintKey: "palette.hint.knowledge",
  },
  {
    section: "platform-administration",
    platformModule: "Platform Governance",
    labelKey: "nav.operations",
    href: ROUTES.GOVERNANCE_OPERATIONAL_SETTINGS,
    iconId: "adminOperational",
    requiredPermissions: PERM_ADMIN_ACCESS,
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "platform-administration",
    platformModule: "Platform Governance",
    labelKey: "nav.adminIntegrations",
    href: ROUTES.GOVERNANCE_INTEGRATIONS,
    iconId: "adminIntegrations",
    requiredPermissions: PERM_ADMIN_ACCESS,
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "platform-administration",
    platformModule: "Platform Governance",
    labelKey: "nav.adminSyncDiagnostics",
    href: ROUTES.GOVERNANCE_SYNC_DIAGNOSTICS,
    iconId: "adminSyncDiagnostics",
    requiredPermissions: PERM_ADMIN_ACCESS,
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "knowledge-data-governance",
    platformModule: "Data Governance",
    labelKey: "nav.enterpriseDataCatalog",
    href: ROUTES.GOVERNANCE_CONTEXT_CATALOG,
    iconId: "contextGovernance",
    requiredPermissions: ["knowledge.catalog.read"],
    paletteHintKey: "palette.hint.knowledgeDataGovernance",
  },
  {
    section: "knowledge-data-governance",
    platformModule: "Data Governance",
    labelKey: "nav.knowledgePolicy",
    href: ROUTES.GOVERNANCE_CONTEXT_ENABLEMENT,
    iconId: "contextGovernance",
    requiredPermissions: ["knowledge.policy.read"],
    paletteHintKey: "palette.hint.knowledgeDataGovernance",
  },
  {
    section: "knowledge-data-governance",
    platformModule: "Data Governance",
    labelKey: "nav.catalogDrift",
    href: ROUTES.GOVERNANCE_CONTEXT_DRIFT,
    iconId: "contextGovernance",
    requiredPermissions: ["knowledge.catalog.read"],
    paletteHintKey: "palette.hint.knowledgeDataGovernance",
  },
  {
    section: "knowledge-data-governance",
    platformModule: "Data Governance",
    labelKey: "nav.projectionControl",
    href: ROUTES.GOVERNANCE_CONTEXT_OPS,
    iconId: "contextGovernance",
    requiredPermissions: ["knowledge.ops.read"],
    paletteHintKey: "palette.hint.knowledgeDataGovernance",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamAuthz",
    href: ROUTES.ADMIN_AUTHZ,
    iconId: "iamAuthz",
    requiredPermissions: ["authz.catalog.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamTenants",
    href: ROUTES.ADMIN_AUTHZ_TENANTS,
    iconId: "iamAuthz",
    parentHref: ROUTES.ADMIN_AUTHZ,
    requiredPermissions: ["authz.tenants.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamServices",
    href: ROUTES.ADMIN_AUTHZ_SERVICES,
    iconId: "iamAuthz",
    parentHref: ROUTES.ADMIN_AUTHZ,
    requiredPermissions: ["authz.catalog.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamServiceItems",
    href: ROUTES.ADMIN_AUTHZ_SERVICE_ITEMS,
    iconId: "iamAuthz",
    parentHref: ROUTES.ADMIN_AUTHZ,
    requiredPermissions: ["authz.catalog.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamSync",
    href: ROUTES.ADMIN_AUTHZ_SYNC,
    iconId: "iamAuthz",
    parentHref: ROUTES.ADMIN_AUTHZ,
    requiredPermissions: ["authz.service_items.write"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamRoles",
    href: ROUTES.ADMIN_AUTHZ_ROLES,
    iconId: "iamAuthzRoles",
    parentHref: ROUTES.ADMIN_AUTHZ,
    requiredPermissions: ["authz.roles.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamUsers",
    href: ROUTES.ADMIN_AUTHZ_USERS,
    iconId: "iamAuthzUsers",
    parentHref: ROUTES.ADMIN_AUTHZ,
    requiredPermissions: ["authz.users.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamSimulator",
    href: ROUTES.ADMIN_AUTHZ_SIMULATOR,
    iconId: "iamAuthz",
    parentHref: ROUTES.ADMIN_AUTHZ,
    requiredPermissions: ["authz.scope.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "iam-access",
    platformModule: "IAM Authority",
    labelKey: "nav.iamAuditLog",
    href: ROUTES.ADMIN_AUTHZ_AUDIT,
    iconId: "iamAuthz",
    parentHref: ROUTES.ADMIN_AUTHZ,
    requiredPermissions: ["authz.audit.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "ai-safety",
    platformModule: "Platform Governance",
    labelKey: "nav.aiAuditTrail",
    href: ROUTES.GOVERNANCE_AI_AUDIT,
    iconId: "aiAudit",
    requiredPermissions: ["governance.ai_audit.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "ai-safety",
    platformModule: "Platform Governance",
    labelKey: "nav.privacyRules",
    href: ROUTES.GOVERNANCE_AI_PRIVACY_RULES,
    iconId: "aiPrivacyRules",
    requiredPermissions: ["governance.llm_privacy.rules.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "ai-safety",
    platformModule: "Platform Governance",
    labelKey: "nav.workflowAiPolicy",
    href: ROUTES.AI_WORKFLOW_POLICY,
    iconId: "workflowAiPolicy",
    requiredPermissions: ["llm.catalog.read"],
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "template-governance",
    platformModule: "Platform Governance",
    labelKey: "nav.promptTemplates",
    href: ROUTES.GOVERNANCE_DRAFT_TEMPLATES,
    iconId: "draftTemplates",
    requiredPermissions: PERM_PROMPT_TEMPLATES_READ,
    paletteHintKey: "palette.hint.draftTemplates",
  },
  {
    section: "policy-rules",
    platformModule: "Platform Governance",
    labelKey: "nav.templateRouting",
    href: ROUTES.GOVERNANCE_TEMPLATE_ROUTING_RULES,
    iconId: "customers",
    requiredPermissions: PERM_PROMPT_TEMPLATES_READ,
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "ai-platform",
    platformModule: "AI Platform",
    labelKey: "nav.aiChat",
    href: ROUTES.AI_CHAT,
    iconId: "ranking",
    requiredPermissions: ["ai.chat.execute"],
    paletteHintKey: "palette.hint.aiPlatform",
  },
  {
    section: "ai-platform",
    platformModule: "AI Platform",
    labelKey: "nav.niceTravelAgent",
    href: ROUTES.AI_NICE_TRAVEL,
    iconId: "ranking",
    requiredPermissions: ["ai.booking_agent.execute"],
    paletteHintKey: "palette.hint.aiPlatform",
  },
  {
    section: "ai-platform",
    platformModule: "AI Platform",
    labelKey: "nav.aiTextGeneration",
    href: ROUTES.AI_GENERATE,
    iconId: "ranking",
    requiredPermissions: ["ai.generate.execute"],
    paletteHintKey: "palette.hint.aiPlatform",
  },
  {
    section: "ai-platform",
    platformModule: "AI Platform",
    labelKey: "nav.aiAnalysis",
    href: ROUTES.AI_ANALYSIS,
    iconId: "ranking",
    requiredPermissions: ANALYSIS_REQUIRED_PERMISSIONS,
    paletteHintKey: "palette.hint.aiPlatform",
  },
  {
    section: "ai-platform",
    platformModule: "AI Platform",
    labelKey: "nav.aiModels",
    href: ROUTES.AI_MODELS,
    iconId: "ranking",
    requiredPermissions: ["ai.models.read"],
    paletteHintKey: "palette.hint.aiPlatform",
  },
  {
    section: "ai-platform",
    platformModule: "AI Platform",
    labelKey: "nav.relevanceRanking",
    href: ROUTES.RANKING,
    iconId: "ranking",
    requiredPermissions: RANKING_REQUIRED_PERMISSIONS,
    paletteHintKey: "palette.hint.aiPlatform",
  },
  {
    section: "observability",
    platformModule: "Platform Observability",
    labelKey: "nav.platformAnalytics",
    href: ROUTES.PLATFORM_ANALYTICS,
    iconId: "platformAnalytics",
    paletteHintKey: "palette.hint.platformAnalytics",
    requiredPermissions: PERM_OBSERVABILITY_READ,
  },
  {
    section: "observability",
    platformModule: "Platform Observability",
    labelKey: "nav.monitoring",
    href: ROUTES.PLATFORM_MONITORING,
    iconId: "monitoring",
    paletteHintKey: "palette.hint.monitoring",
    requiredPermissions: PERM_OBSERVABILITY_READ,
  },
  {
    section: "intake",
    platformModule: "Intake Core",
    labelKey: "nav.ledger",
    href: ROUTES.INTAKE_LEDGER,
    iconId: "ledger",
    requiredPermissions: PERM_INTAKE_LEDGER_READ,
    paletteHintKey: "palette.hint.platformAdmin",
  },
  {
    section: "intake",
    platformModule: "Intake Core",
    labelKey: "nav.mailbox",
    href: ROUTES.MAILBOX,
    iconId: "inbox",
    requiredPermissions: PERM_SETTINGS_INTEGRATIONS_READ,
    paletteHintKey: "palette.hint.platformAdmin",
  },
];
export interface CommandPaletteExtraItem {
  id: string;
  labelKey: string;
  path: string;
  hintKey?: string;
}

export const COMMAND_PALETTE_EXTRAS: readonly CommandPaletteExtraItem[] = [{ id: "tasks-my", labelKey: "palette.tasksMy", path: workTasksMyListPath(), hintKey: "palette.hint.tasksScope" }];

export interface SidebarFooterNavItem {
  labelKey: string;
  href: string;
  iconId: SidebarNavIconId;
  openInNewTab?: boolean;
}

/** Pinned footer link — visible to every authenticated user (no permission gate). */
export const SIDEBAR_FOOTER_NAV: readonly SidebarFooterNavItem[] = [{ labelKey: "nav.userGuide", href: ROUTES.USER_GUIDE, iconId: "userGuide", openInNewTab: true }];
